import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideExperimentalZonelessChangeDetection,
} from '@angular/core';
import {
  PreloadAllModules,
  RouteReuseStrategy,
  provideRouter,
  withComponentInputBinding,
  withPreloading,
} from '@angular/router';
import {IonicModule, IonicRouteStrategy} from '@ionic/angular';
import {appRoutes} from './app.routes';
import {provideTranslation} from '@ew/translation';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {EW_TOASTER} from '@ew/ew-injects';
import {ToasterService} from '@ew-mobile/toaster';
import {provideHttpClient, withInterceptors} from '@angular/common/http';
import {jwtInterceptor} from '@ew/jwt';
import {provideAppConfig} from '@ew/app-config';
import {environment} from '../environments/environment';
import {SocketIoConfig, SocketIoModule} from 'ngx-socket-io';
import {EwSocket} from '@ew/enums';
import {provideHotToastConfig} from '@ngxpert/hot-toast';
import {IonicStorageModule} from '@ionic/storage-angular';
import {Storage} from '@ionic/storage-angular';

/**
 * WebSocket Configuration for Real-Time Communication
 *
 * Configures the Socket.IO client for real-time bidirectional communication
 * between the mobile app and the backend server. Used for:
 * - Real-time wait time updates
 * - Queue position notifications
 * - Live chat messages between customers and staff
 * - Push notifications for booking status changes
 *
 * The URL is constructed from the environment's base API URL, removing the
 * '/api' suffix and appending the WebSocket namespace.
 *
 * Mobile Considerations:
 * - Connection persists across app backgrounding/foregrounding
 * - Automatically reconnects on network changes
 * - Handles mobile network transitions (WiFi <-> cellular)
 */
const socketConfig: SocketIoConfig = {
  url: `${environment.baseURL.split('/api')[0]}${EwSocket.Namespace}`,
  options: {},
};

/**
 * Mobile Application Configuration
 *
 * This is the core configuration object for the Enterwait mobile application.
 * It provides all the necessary providers, services, and configurations required
 * for the mobile app to function properly.
 *
 * Key Configuration Areas:
 *
 * 1. Ionic Framework Setup:
 *    - IonicModule: Provides Ionic UI components and mobile navigation
 *    - IonicRouteStrategy: Custom routing strategy optimized for mobile navigation stacks
 *    - IonicStorageModule: Persistent storage using native SQLite or IndexedDB
 *
 * 2. Capacitor Integration:
 *    - Enables native mobile features (camera, geolocation, push notifications)
 *    - Provides bridge between web code and native iOS/Android APIs
 *    - Storage plugin for offline data persistence
 *
 * 3. Performance Optimizations:
 *    - Zone-less change detection for faster rendering on mobile devices
 *    - PreloadAllModules strategy to load routes in background after initial load
 *    - OnPush change detection strategy throughout the app
 *
 * 4. Real-Time Communication:
 *    - Socket.IO for WebSocket connections
 *    - Real-time wait time updates and notifications
 *    - Live chat functionality
 *
 * 5. HTTP & Authentication:
 *    - HTTP client with JWT interceptor for API authentication
 *    - Automatic token refresh and request retries
 *    - Secure token storage in Ionic Storage
 *
 * 6. Mobile-Specific UI:
 *    - Hot Toast for mobile-optimized toast notifications (bottom-center position)
 *    - ToasterService for consistent notification UX across the app
 *    - BrowserAnimations for smooth transitions and gestures
 *
 * 7. Internationalization:
 *    - Translation service for multi-language support
 *    - Language selection persisted in local storage
 *
 * 8. Initialization:
 *    - App initializer to set up Ionic Storage before app bootstrap
 *    - Ensures storage is ready before any components try to use it
 *
 * Mobile-Specific Considerations:
 * - All providers are optimized for mobile performance
 * - Storage uses native SQLite on iOS/Android for better performance
 * - Route preloading balances initial load time with smooth navigation
 * - Toast notifications positioned at bottom-center for thumb accessibility
 * - WebSocket reconnection handles mobile network transitions
 *
 * @see IonicModule - Ionic Framework core module
 * @see IonicStorageModule - Persistent key-value storage for mobile
 * @see SocketIoModule - Real-time WebSocket communication
 * @see provideExperimentalZonelessChangeDetection - Performance optimization
 */
export const appConfig: ApplicationConfig = {
  providers: [
    // Environment configuration (API URLs, feature flags, etc.)
    provideAppConfig(environment),

    // Browser animations for smooth transitions and gesture-based interactions
    importProvidersFrom([BrowserAnimationsModule]),

    // Real-time WebSocket communication for live updates
    // Used for: queue updates, chat messages, notifications
    importProvidersFrom(SocketIoModule.forRoot(socketConfig)),

    // Ionic Framework configuration
    // - IonicModule: Core Ionic UI components and navigation
    // - IonicStorageModule: Persistent storage using native SQLite (iOS/Android) or IndexedDB (web)
    importProvidersFrom(IonicModule.forRoot({}), IonicStorageModule.forRoot()),

    // Internationalization support for multi-language mobile app
    provideTranslation(),

    // Toast notification configuration optimized for mobile devices
    // - bottom-center: Accessible with thumb on large phones
    // - dismissible: User can swipe to dismiss
    // - reverseOrder: Newest toasts appear at top of stack
    provideHotToastConfig({
      reverseOrder: true,
      dismissible: true,
      position: 'bottom-center', // Mobile-friendly position
    }),

    // HTTP client with JWT authentication interceptor
    // Automatically adds authentication tokens to API requests
    provideHttpClient(withInterceptors([jwtInterceptor])),

    // Zoneless change detection for improved mobile performance
    // Reduces overhead of Angular's zone.js on resource-constrained devices
    provideExperimentalZonelessChangeDetection(),

    // Ionic-specific routing strategy for mobile navigation
    // Handles back button, navigation stacks, and modal navigation
    {provide: RouteReuseStrategy, useClass: IonicRouteStrategy},

    // Router configuration with mobile optimizations
    // - PreloadAllModules: Preload lazy routes in background after initial load
    // - withComponentInputBinding: Enable route params as component inputs
    provideRouter(appRoutes, withPreloading(PreloadAllModules), withComponentInputBinding()),

    // Mobile-optimized toaster service for notifications
    // Uses Ionic toast component with platform-specific styling
    {
      provide: EW_TOASTER,
      useClass: ToasterService,
    },

    // App initialization: Set up Ionic Storage before app starts
    // Critical for mobile apps that need to read user preferences/tokens on startup
    provideAppInitializer(async () => {
      const storage = inject(Storage);

      // Create/initialize the storage instance
      // On iOS/Android: Creates SQLite database
      // On web: Creates IndexedDB database
      await storage.create();
    }),
  ],
};
